import React from "react";
import PropTypes from "prop-types";
import { Link, Redirect } from "react-router-dom";
import classNames from "classnames";

import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";

import commons from "../common/commons";
import {
  EuropeanStockBadge,
  EuropeanStockEta,
  EuropeanStockNotice,
  europeanStockCardStyles,
} from "../common/EuropeanStockMark";
import BrandLogoImage from "../common/BrandLogoImage";
import {
  PAIR_KIND,
  getPairKind,
  getPairEuropeanDetails,
  getPairProducts,
  getPairPrice,
  getPairBrand,
  getPairTitle,
  formatIls,
} from "./tirePairModel";
import {
  formatTireSizeLabel,
  parseSetAxleSizes,
  readProductScore,
  getSharedPairScore,
} from "./tirePairDisplay";
import tireCardStyles from "./tireCardStyles";
import {
  AvailabilityBadge,
  AxleRow,
  ScoreMark,
  productPath,
} from "./TireCardParts";

const KIND_LABEL = {
  [PAIR_KIND.SET]: "סט מקורי",
  [PAIR_KIND.PAIR]: "זוג תואם",
};

const styles = (theme) => ({
  ...tireCardStyles(theme),
  ...europeanStockCardStyles(),
});

function tireImageUrl(product) {
  if (!product) {
    return "";
  }

  const photo = product.photos && product.photos[0];
  if (photo && photo.url) {
    return photo.url;
  }

  return commons.getProductPrimaryImage(
    product.cdn,
    product.ean,
    product.source === "georgia"
  ).url;
}

function backgroundImage(url) {
  if (!url) {
    return "none";
  }

  return `url("${String(url).replace(/"/g, "%22")}")`;
}

function detailsProduct(pair) {
  if (getPairKind(pair) === PAIR_KIND.SET) {
    return pair.set;
  }

  return (pair && pair.front) || (pair && pair.rear) || null;
}

function cartItems(pair) {
  if (getPairKind(pair) === PAIR_KIND.SET) {
    if (!pair.set || !pair.set.ean) {
      return [];
    }

    return [{ ean: pair.set.ean, isSet: true, isAtvSet: false }];
  }

  return [pair.front, pair.rear]
    .filter((product) => product && product.ean)
    .map((product) => ({ ean: product.ean, isSet: false, isAtvSet: false }));
}

function canOrderPair(pair) {
  if (!pair) {
    return false;
  }

  if (getPairKind(pair) === PAIR_KIND.SET) {
    return Boolean(pair.set && pair.set.ean);
  }

  return Boolean(pair.front && pair.front.ean && pair.rear && pair.rear.ean);
}

function pairShipsFree(pair) {
  const products = getPairProducts(pair);
  return products.length > 0 && products.every((product) => Boolean(product.free_shipping));
}

function pairHasLimitedOffer(pair, offers) {
  if (!Array.isArray(offers) || offers.length === 0) {
    return false;
  }

  const eans = {};
  getPairProducts(pair).forEach((product) => {
    if (product && product.ean) {
      eans[product.ean] = true;
    }
  });

  return offers.some((offer) => offer && eans[offer.ean]);
}

function cardTitle(pair) {
  const product = detailsProduct(pair);
  return getPairTitle(pair)
    || (product && (product.displayName || product.name))
    || getPairBrand(pair)
    || "";
}

function mediaItems(pair, title) {
  if (getPairKind(pair) === PAIR_KIND.SET) {
    return [{
      key: "set",
      url: tireImageUrl(pair.set),
      label: title || "תמונת סט צמיגים",
      caption: null,
    }];
  }

  const items = [];
  if (pair.front) {
    items.push({
      key: "front",
      url: tireImageUrl(pair.front),
      label: `צמיג קדמי ${title}`.trim(),
      caption: "קדמי",
    });
  }
  if (pair.rear) {
    items.push({
      key: "rear",
      url: tireImageUrl(pair.rear),
      label: `צמיג אחורי ${title}`.trim(),
      caption: "אחורי",
    });
  }

  return items;
}

const CARD_SIZE_SEPARATOR = " · ";

function axleSize(size, load, speed) {
  return formatTireSizeLabel(size, load, speed, CARD_SIZE_SEPARATOR);
}

function axleRows(pair) {
  const sharedScore = getSharedPairScore(pair);

  if (getPairKind(pair) === PAIR_KIND.SET) {
    const product = pair.set;
    const href = productPath(product);
    const parsed = parseSetAxleSizes(product && product.displaySize);

    if (parsed) {
      return [
        { key: "front", label: "קדמי", size: axleSize(parsed.front, product.frontLoad, product.frontSpeed), product, price: null, href, score: null, showBadge: false },
        { key: "rear", label: "אחורי", size: axleSize(parsed.rear, product.rearLoad, product.rearSpeed), product, price: null, href, score: null, showBadge: false },
      ];
    }

    if (product && product.displaySize) {
      return [{
        key: "size",
        label: null,
        size: axleSize(product.displaySize, product.load, product.speed),
        product,
        price: null,
        href,
        score: null,
        showBadge: false,
      }];
    }

    return [];
  }

  const rows = [];
  if (pair.front) {
    rows.push({
      key: "front",
      label: "קדמי",
      size: axleSize(pair.front.displaySize, pair.front.load || pair.front.loadIndex, pair.front.speed || pair.front.speedIndex),
      product: pair.front,
      price: pair.front.price,
      href: productPath(pair.front),
      score: sharedScore == null ? readProductScore(pair.front) : null,
      showBadge: true,
    });
  }
  if (pair.rear) {
    rows.push({
      key: "rear",
      label: "אחורי",
      size: axleSize(pair.rear.displaySize, pair.rear.load || pair.rear.loadIndex, pair.rear.speed || pair.rear.speedIndex),
      product: pair.rear,
      price: pair.rear.price,
      href: productPath(pair.rear),
      score: sharedScore == null ? readProductScore(pair.rear) : null,
      showBadge: true,
    });
  }

  return rows;
}

function PairMedia({ classes, items }) {
  return (
    <div className={classes.media}>
      {items.map((item) => (
        <div key={item.key} className={classes.imageFrame}>
          <div
            className={classes.image}
            style={{ backgroundImage: backgroundImage(item.url) }}
            role="img"
            aria-label={item.label}
          />
          {item.caption ? (
            <Typography variant="caption" component="span" className={classes.imageCaption}>
              {item.caption}
            </Typography>
          ) : null}
        </div>
      ))}
    </div>
  );
}

PairMedia.propTypes = {
  classes: PropTypes.object.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    url: PropTypes.string,
    label: PropTypes.string,
    caption: PropTypes.string,
  })).isRequired,
};

class TirePairCard extends React.Component {
  ordering = false;

  state = {
    ordering: false,
    redirectToCart: false,
  };

  componentDidMount() {
    this.preloadImages();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pair !== this.props.pair) {
      this.preloadImages();
    }
  }

  preloadImages() {
    mediaItems(this.props.pair, cardTitle(this.props.pair)).forEach((item) => {
      if (!item.url) {
        return;
      }

      const img = new Image();
      img.src = item.url;
    });
  }

  orderSet = async () => {
    if (this.ordering || this.state.ordering) {
      return;
    }

    const { pair, addProductsToCart, collectMetric } = this.props;
    if (!canOrderPair(pair)) {
      return;
    }

    const items = cartItems(pair);
    this.ordering = true;
    this.setState({ ordering: true });

    try {
      const added = await addProductsToCart(items);
      if (!added) {
        this.ordering = false;
        this.setState({ ordering: false });
        return;
      }

      items.forEach((item) => {
        collectMetric({
          kind: "PRODUCT_ADD_TO_CART",
          source: "pair_list_page",
          ean: item.ean,
        });
      });

      this.setState({ redirectToCart: true });
    } catch (error) {
      this.ordering = false;
      this.setState({ ordering: false });
      return error;
    }
  };

  render() {
    const { classes, pair, limitedTimeOffers } = this.props;
    const { ordering, redirectToCart } = this.state;

    if (redirectToCart) {
      return <Redirect to="/cart" />;
    }

    const kind = getPairKind(pair);
    const title = cardTitle(pair);
    const brand = getPairBrand(pair);
    const titlePath = productPath(detailsProduct(pair));
    const sharedScore = getSharedPairScore(pair);
    const rows = axleRows(pair);
    const images = mediaItems(pair, title);
    const freeShipping = pairShipsFree(pair);
    const limitedOffer = pairHasLimitedOffer(pair, limitedTimeOffers);
    const orderLabel = "הוסף סט לסל";
    const europeanDetails = getPairEuropeanDetails(pair, this.props.availabilityContext);
    const isEu = Boolean(europeanDetails);

    return (
      <article
        className={classNames(classes.card, isEu && classes.euCard)}
        dir="rtl"
        aria-label={title || orderLabel}
      >
        <PairMedia classes={classes} items={images} />
        <div className={classes.details}>
          <div className={classes.heading}>
            {brand ? (
              <BrandLogoImage
                brand={brand}
                alt={brand}
                className={classes.brandLogo}
                fallback={(
                  <Typography variant="overline" component="span" className={classes.brandFallback}>
                    {brand}
                  </Typography>
                )}
              />
            ) : null}
            <div className={brand ? classes.headingText : undefined}>
              <Typography variant="h6" component="h2" className={classes.title}>
                {titlePath ? (
                  <Link to={titlePath} className={classes.titleLink}>
                    {title}
                  </Link>
                ) : title}
              </Typography>
              <div className={classes.meta}>
                <Typography
                  variant="caption"
                  component="span"
                  className={classNames(
                    classes.kindChip,
                    classes.metaItem,
                    kind === PAIR_KIND.SET ? classes.kindChipSet : classes.kindChipPair
                  )}
                >
                  {KIND_LABEL[kind]}
                </Typography>
                {isEu ? (
                  <EuropeanStockBadge
                    label={europeanDetails.label}
                    className={classes.metaItem}
                  />
                ) : kind === PAIR_KIND.SET ? (
                  <AvailabilityBadge
                    classes={classes}
                    product={pair.set}
                    className={classes.metaItem}
                  />
                ) : null}
                {sharedScore != null ? (
                  <ScoreMark classes={classes} score={sharedScore} className={classes.metaItem} />
                ) : null}
                {limitedOffer ? (
                  <Typography variant="caption" component="span" className={classNames(classes.offerChip, classes.metaItem)}>
                    מבצע לזמן מוגבל
                  </Typography>
                ) : null}
              </div>
            </div>
          </div>
          {rows.length > 0 ? (
            <div className={classes.axles}>
              {rows.map((row) => (
                <AxleRow key={row.key} classes={classes} row={row} />
              ))}
            </div>
          ) : null}
          {isEu ? <EuropeanStockNotice details={europeanDetails} /> : null}
        </div>
        <div className={classes.cta}>
          <Typography variant="overline" component="div" className={classes.priceLabel}>
            מחיר לסט (2 צמיגים)
          </Typography>
          <Typography variant="h5" component="p" className={classes.totalPrice}>
            <bdi dir="ltr">{formatIls(getPairPrice(pair))}</bdi>
          </Typography>
          {freeShipping ? (
            <div className={classes.shipping}>
              <LocalShippingIcon className={classes.shippingIcon} fontSize="small" aria-hidden="true" />
              <Typography variant="body2" component="span" className={classes.shippingText}>
                משלוח חינם
              </Typography>
            </div>
          ) : null}
          {isEu ? <EuropeanStockEta label={europeanDetails.arrivalLabel} /> : null}
          <Button
            type="button"
            variant="contained"
            color="primary"
            fullWidth
            disableElevation
            className={classes.orderButton}
            disabled={ordering || !canOrderPair(pair)}
            onClick={this.orderSet}
            aria-label={title ? `${orderLabel}: ${title}` : orderLabel}
            aria-busy={ordering}
          >
            {ordering ? <CircularProgress size={20} color="inherit" /> : orderLabel}
          </Button>
          {titlePath ? (
            <Button
              component={Link}
              to={titlePath}
              color="primary"
              fullWidth
              className={classes.detailsButton}
              aria-label={title ? `פרטים נוספים על ${title}` : "פרטים נוספים"}
            >
              פרטים נוספים
            </Button>
          ) : null}
        </div>
      </article>
    );
  }
}

TirePairCard.propTypes = {
  pair: PropTypes.object.isRequired,
  addProductsToCart: PropTypes.func.isRequired,
  collectMetric: PropTypes.func.isRequired,
  limitedTimeOffers: PropTypes.array,
  availabilityContext: PropTypes.shape({
    currentShipment: PropTypes.string,
    nextShipment: PropTypes.string,
    euContainerMode: PropTypes.object,
  }),
};

TirePairCard.defaultProps = {
  limitedTimeOffers: null,
  availabilityContext: {},
};

export default withStyles(styles)(TirePairCard);
